import React, { useLayoutEffect, useRef, useState } from "react";

const SMART_SPEED = 250;
const sameList = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);

/**
 * React stand-in for the reference's Owl Carousel 2.3.4 in `autoWidth` mode (the Andar Bahar 50 / 150
 * video sliders: rtl, margin 2, slideBy 3), rendering Owl's initialised markup so the ported CSS applies:
 *   div#id.{className}.owl-rtl.owl-loaded.owl-drag
 *     > .owl-stage-outer > .owl-stage[style transform/transition/width]
 *       > .owl-item[.active][style width:auto; margin-left] × n
 *     + .owl-nav > button.owl-prev + button.owl-next   + .owl-dots.disabled
 * Owl's autoWidth maths: item width = the item's own width, coordinates accumulate width + margin,
 * stage width = ceil(last coordinate), maximum = first index from which the tail fits the element,
 * `.active` = items whose start or end lies inside [begin, begin + element width).
 * `items` is Owl's responsive count (only decides `.owl-nav.disabled`).
 * `resetKey`: like the reference's "replace.owl.carousel" on new data, a change jumps back to 0.
 */
export default function AbOwl({ id, className, margin = 2, slideBy = 3, items = 4, resetKey, children }) {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  const [widths, setWidths] = useState([]);
  const [pos, setPos] = useState({ current: 0, speed: 0, key: resetKey });
  const slides = React.Children.toArray(children);
  // replace.owl.carousel with "<div/>" when there are no cards: one empty item
  const list = slides.length ? slides : [<div key="empty" />];
  const count = list.length;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => setWidth(el.getBoundingClientRect().width);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Owl reads every item's own (auto) width (re-read when the element or the slides change)
  const sig = list.map((sl) => sl.key).join("|");
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const next = [...el.querySelectorAll(":scope > .owl-stage-outer > .owl-stage > .owl-item")].map(
      (n) => n.getBoundingClientRect().width
    );
    setWidths((prev) => (sameList(prev, next) ? prev : next));
  }, [sig, width]);

  let { current, speed } = pos;
  if (pos.key !== resetKey) {
    current = 0;
    speed = 0;
    setPos({ current: 0, speed: 0, key: resetKey });
  }

  const coords = [];
  widths.slice(0, count).reduce((acc, w, i) => {
    coords[i] = acc + w + margin;
    return coords[i];
  }, 0);

  // Owl (autoWidth, no loop): walk back from the last item until the tail no longer fits
  let maximum = 0;
  if (widths.length >= count && count) {
    let it = count - 1;
    let recip = widths[it];
    while (it-- > 0) {
      recip += widths[it] + margin;
      if (recip > width) break;
    }
    maximum = it + 1;
  }
  current = Math.min(current, maximum);
  const coordinate = current > 0 ? Math.ceil(coords[current - 1] || 0) : 0;
  const stageWidth = coords.length ? Math.ceil(coords[coords.length - 1]) : 0;

  const go = (next) => {
    const target = Math.max(0, Math.min(maximum, current + (next ? slideBy : -slideBy)));
    if (target === current) return;
    const s = Math.min(Math.max(Math.abs(target - current), 1), 6) * SMART_SPEED;
    setPos({ current: target, speed: s, key: resetKey });
  };

  const begin = coordinate;
  const end = begin + width;
  const isActive = (i) => {
    // the "<div/>" placeholder of an empty slider is always the active item
    if (!slides.length) return true;
    if (!width || coords.length < count) return false;
    const inner = i > 0 ? coords[i - 1] : 0;
    const outer = coords[i];
    return (inner >= begin && inner < end) || (outer > begin && outer < end);
  };

  return (
    <div ref={ref} id={id} className={`${className} owl-rtl owl-loaded owl-drag`}>
      <div className="owl-stage-outer">
        <div
          className="owl-stage"
          style={{
            transform: `translate3d(${coordinate}px, 0px, 0px)`,
            transition: `${speed / 1000}s`,
            width: `${stageWidth}px`,
          }}
        >
          {list.map((slide, i) => (
            <div key={i} className={isActive(i) ? "owl-item active" : "owl-item"} style={{ width: "auto", marginLeft: `${margin}px` }}>
              {slide}
            </div>
          ))}
        </div>
      </div>
      <div className={count <= items ? "owl-nav disabled" : "owl-nav"}>
        <button type="button" role="presentation" className={current <= 0 ? "owl-prev disabled" : "owl-prev"} onClick={() => go(false)}>
          <span aria-label="Previous">‹</span>
        </button>
        <button
          type="button"
          role="presentation"
          className={current >= maximum ? "owl-next disabled" : "owl-next"}
          onClick={() => go(true)}
        >
          <span aria-label="Next">›</span>
        </button>
      </div>
      <div className="owl-dots disabled" />
    </div>
  );
}
